import React, { useEffect, useRef } from 'react';
import { Box, Button, TextField, Typography, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  navigator: {
    position: 'sticky',
    top: 'var(--app-header-height, 64px)',
    zIndex: theme.zIndex.appBar - 1,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 2)
  },
  phaseSelect: { flex: '2 1 220px', minWidth: 0 },
  groupSelect: { flex: '1 1 150px', minWidth: 0 },
  group: {
    marginTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2)
  }
}));

export function scrollToScheduleSection(target) {
  if (!target) return;
  const headerHeight = document.getElementById('app-header')?.getBoundingClientRect().height ?? 0;
  const navigator = document.getElementById('schedule-navigator');
  const navigatorHeight = target === navigator ? 0 : navigator?.getBoundingClientRect().height ?? 0;
  window.scrollTo({
    top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerHeight - navigatorHeight - 16),
    behavior: 'smooth'
  });
}

export function ScheduleGroup({ phaseId, group, children }) {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <section id={`schedule-phase-${phaseId}-${group}`} className={classes.group}
      aria-labelledby={`schedule-heading-${phaseId}-${group}`}>
      <Typography variant="h6" component="h3" id={`schedule-heading-${phaseId}-${group}`}>
        {t(group)}
      </Typography>
      {children}
    </section>
  );
}

export default function ScheduleNavigator({ phases, selectedPhaseId, groups, selectedGroup, onNavigate, onGroupVisible }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigator = useRef(null);
  const groupKey = groups.join(',');
  useEffect(() => {
    let frame;
    const update = () => {
      frame = undefined;
      const edge = navigator.current?.getBoundingClientRect().bottom;
      const sections = groupKey.split(',').map((group) => ({ group,
        rect: document.getElementById(`schedule-phase-${selectedPhaseId}-${group}`)?.getBoundingClientRect()
      })).filter(({ rect }) => rect && rect.height > 0);
      if (!sections.length || edge === undefined) return;
      // Select the section passing below the sticky navigation. This changes
      // only the selection; scrolling is reserved for an explicit menu choice.
      let visible = sections[0];
      for (const section of sections) {
        if (section.rect.top <= edge + 24) visible = section;
      }
      if (window.scrollY > 0 && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2)
        visible = sections[sections.length - 1];
      if (visible.group !== selectedGroup) onGroupVisible(visible.group);
    };
    const requestUpdate = () => {
      if (frame === undefined) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [groupKey, selectedPhaseId, selectedGroup, onGroupVisible]);
  const selectedIndex = phases.findIndex((phase) => phase.id === selectedPhaseId);
  return (
    <Box ref={navigator} component="nav" id="schedule-navigator" aria-label={t('schedule_navigator')}
      className={classes.navigator}>
      <TextField select SelectProps={{ native: true }} className={classes.phaseSelect}
        label={t('phase')} value={selectedPhaseId ?? ''}
        inputProps={{ 'aria-label': t('schedule_select_phase') }}
        onChange={(event) => onNavigate(Number(event.target.value), selectedGroup)}>
        {phases.map((phase) => (
          <option key={phase.id} value={phase.id}>
            {`${t('phase')} ${phase.number}${phase.name ? ` · ${phase.name}` : ''} — ${t('day')}: ${phase.days}`}
          </option>
        ))}
      </TextField>
      <TextField select SelectProps={{ native: true }} className={classes.groupSelect}
        label={t('schedule_group')} value={selectedGroup || ''} disabled={!groups.length}
        inputProps={{ 'aria-label': t('schedule_select_group') }}
        onChange={(event) => onNavigate(selectedPhaseId, event.target.value)}>
        {!groups.length && <option value="">{t('schedule_no_groups')}</option>}
        {groups.map((group) => <option key={group} value={group}>{t(group)}</option>)}
      </TextField>
      <Box display="flex" flexWrap="wrap">
        <Button size="small" color="primary" disabled={selectedIndex <= 0}
          onClick={() => onNavigate(phases[selectedIndex - 1].id, selectedGroup)}>
          {t('schedule_previous_phase')}
        </Button>
        <Button size="small" color="primary" disabled={selectedIndex < 0 || selectedIndex >= phases.length - 1}
          onClick={() => onNavigate(phases[selectedIndex + 1].id, selectedGroup)}>
          {t('schedule_next_phase')}
        </Button>
      </Box>
    </Box>
  );
}
